import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Modal } from "@msys/ui";
import { DialogContentText, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { OrganisationMemberSelect } from "../../organisations/OrganisationMemberSelect.js";
import { useProjectRoles } from "../../users/useRoles.js";
import {
  AddProjectMemberModal_OrganisationMemberDocument,
  AddProjectMemberModal_OrganisationMemberQuery,
  AddProjectMemberModal_OrganisationMemberQueryVariables,
  useAddProjectMemberModal_AddProjectMemberMutation,
  useAddProjectMemberModalQuery,
} from "./AddProjectMemberModal.generated.js";

interface FormValues {
  userId: string | null;
  roleId: string | null;
}

interface Props {
  projectId: string;
  title?: string;
  handleClose: () => void;
  handleComplete?: (userId: string) => Promise<void> | void;
  refetchQueries?: string[];
}

export const AddProjectMemberModal = ({
  projectId,
  title,
  handleClose,
  handleComplete,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["ProjectMembers", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const { getProjectRoleOptions } = useProjectRoles();

  const client = useApolloClient();
  const query = useAddProjectMemberModalQuery({
    client,
    variables: { projectId },
  });

  const project = getDataOrNull(query.data?.project)?.project;

  const allRoles = project?.roles ?? [];
  const internalStakeholders = project?.internalStakeholders ?? [];

  const roleOptions = getProjectRoleOptions(allRoles);

  const [addProjectMember] = useAddProjectMemberModal_AddProjectMemberMutation({
    client,
  });

  const handleSubmit = async (values: FormValues) => {
    await addProjectMember({
      variables: {
        projectId: projectId,
        userId: values.userId!,
        roleIds: [values.roleId!],
      },
      refetchQueries,
      awaitRefetchQueries: true,
    });
    await handleComplete?.(values.userId!);
    handleClose();
    enqueueSnackbar(
      t("Project member added", {
        ns: "ProjectMembers",
      })
    );
  };

  const formId = React.useMemo(() => uniqueId(), []);

  const initialValues = {
    userId: null,
    roleId: null,
  };

  const validationSchema = Yup.object().shape({
    userId: Yup.string()
      .label(
        t("Organisation Member", {
          ns: "ProjectMembers",
        })
      )
      .required(),
    roleId: Yup.string()
      .label(
        t("Project roles", {
          ns: "ProjectMembers",
        })
      )
      .required(),
  });

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
    >
      {({
        isValid,
        values,
        setFieldValue,
        isSubmitting,
      }: FormikProps<FormValues>) => (
        <Modal
          title={
            title ??
            t("Add project member", {
              ns: "ProjectMembers",
            })
          }
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Send", {
                ns: "Global",
              }),
              buttonProps: {
                type: "submit",
                form: formId,
                disabled: !isValid || query.loading,
                loading: isSubmitting,
              },
            },
          ]}
          isLoading={query.loading}
        >
          <Form id={formId}>
            <DialogContentText>
              {t(
                "Here you can add one of your organisation members to the project, and collaborate together on the work!",
                {
                  ns: "ProjectMembers",
                }
              )}
            </DialogContentText>
            <Stack spacing={1} direction="column">
              <OrganisationMemberSelect
                inputLabel={t("Organisation Member", {
                  ns: "ProjectMembers",
                })}
                required
                filters={{
                  excludeIds: internalStakeholders.map(s => s.user.id),
                }}
                value={values.userId}
                onChange={async userId => {
                  setFieldValue("userId", userId);

                  if (userId !== null) {
                    const result = await client.query<
                      AddProjectMemberModal_OrganisationMemberQuery,
                      AddProjectMemberModal_OrganisationMemberQueryVariables
                    >({
                      query: AddProjectMemberModal_OrganisationMemberDocument,
                      variables: { userId },
                    });

                    const member = getDataOrNull(
                      result.data?.organisationMemberships
                    )?.edges[0].node;

                    if (!member) {
                      throw new Error("Member not found");
                    }

                    const role =
                      (member.defaultProjectRoles &&
                        member.defaultProjectRoles.length > 0 &&
                        roleOptions.find(role =>
                          member.defaultProjectRoles.some(
                            defaultProjectRole =>
                              defaultProjectRole.internalName ===
                              role.internalName
                          )
                        )) ??
                      null;

                    if (role) {
                      setFieldValue("roleId", role.value);
                    }
                  }
                }}
              />
              <SelectField
                disabled={values.userId === null}
                label={t("Project roles", {
                  ns: "ProjectMembers",
                })}
                name="roleId"
                options={roleOptions}
                required
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
